import React from 'react';

import PropTypes from 'prop-types';

import { variantSwitch } from '@adsk/alloy-react-helpers';
import {
  PersonFilledIcon,
  PersonIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import theme, { StyleProp, stylePropType } from '@adsk/alloy-react-theme';

import Avatar from '../Avatar';

type AvatarSizes = typeof Avatar.SIZES;
type AvatarSize = AvatarSizes[keyof AvatarSizes];

const renderPersonFilledIcon: RenderIconType = (p) => (
  <PersonFilledIcon {...p} />
);
const renderPersonIcon: RenderIconType = (p) => <PersonIcon {...p} />;

function getIconBySize(size: AvatarSize) {
  return size === Avatar.SIZES.SMALL_16 || size === Avatar.SIZES.MEDIUM_24
    ? renderPersonFilledIcon
    : renderPersonIcon;
}

type DefaultAvatarProps = {
  style?: StyleProp;
  className?: string;
  size?: AvatarSize;
};

const DefaultAvatar = ({
  style,
  className,
  size = Avatar.SIZES.MEDIUM_32,
}: DefaultAvatarProps) => (
  <Avatar
    className={className}
    size={size}
    style={[
      {
        color: theme.tokens.colors.icon.tertiary.value,
        backgroundColor:
          theme.tokens.colors.background.accent.six.subtler.value,
      },
      style,
    ]}
  >
    {getIconBySize(size)({
      size:
        variantSwitch({
          statement: size,
          variants: {
            [Avatar.SIZES.SMALL_16]: 10,
            [Avatar.SIZES.MEDIUM_24]: 15,
            [Avatar.SIZES.MEDIUM_32]: 25,
            [Avatar.SIZES.LARGE_48]: 40,
            [Avatar.SIZES.XLARGE_64]: 50,
            [Avatar.SIZES.XXLARGE_128]: 100,
          },
        }) || undefined,
    })}
  </Avatar>
);

DefaultAvatar.SIZES = Avatar.SIZES;
DefaultAvatar.displayName = 'DefaultAvatar';

DefaultAvatar.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Set the size of the avatar */
  size: PropTypes.oneOf(Object.values(Avatar.SIZES)),
};

export default Object.assign(DefaultAvatar, {
  SIZES: Avatar.SIZES,
});
